// src/screens/ReglesUtilisation.tsx
import React, { useEffect, useState } from "react";
import {
  ShieldCheck, ListChecks, UserCircle2, Ban, Share2, Image as ImageIcon,
  Crown, Lock, Wrench, RefreshCcw, TriangleAlert, Link as LinkIcon,
  Scale, Mail, Sparkles,
} from "lucide-react";
import i18n from "../i18n";

// mini hook i18n safe (évite TS2589)
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

const POLICY = {
  siteName: "SwishData",
  contactEmail: "contact@swishdata.app",
  lastUpdate: "15 août 2025",
  freePlayLimit: 5,
  premiumTeamsLimit: 3,
};

const ReglesUtilisation: React.FC = () => {
  const { t } = useI18nSafe();

  return (
    <section className="relative min-h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Fond déco */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-[-12rem] h-[28rem] w-[60rem] -translate-x-1/2 bg-gradient-to-r from-orange-500/20 via-fuchsia-500/20 to-cyan-500/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.16),_transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.06),_transparent_60%)]" />
      </div>

      <div className="mx-auto max-w-4xl px-4 py-12">
        {/* En-tête */}
        <div className="mx-auto max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-8 text-center shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <div className="mx-auto mb-2 inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Sparkles className="h-4 w-4 text-orange-600 dark:text-orange-300" />
            <span>{t("terms:header.tag")}</span>
          </div>
          <h1 className="text-3xl font-bold italic text-orange-600 dark:text-orange-400">
            {t("terms:header.title", { siteName: POLICY.siteName })}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {t("terms:header.subtitle")}
          </p>
        </div>

        {/* Contenu */}
        <div className="mt-8 space-y-6">
          {/* 1 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s1.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:s1.body", { siteName: POLICY.siteName })}
            </p>
          </div>

          {/* 2 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <UserCircle2 className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s2.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s2.li1")}</li>
              <li>{t("terms:s2.li2")}</li>
              <li>{t("terms:s2.li3")}</li>
              <li>{t("terms:s2.li4", { siteName: POLICY.siteName })}</li>
            </ul>
          </div>

          {/* 3 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s3.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:s3.body")}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s3.li1")}</li>
              <li>{t("terms:s3.li2")}</li>
              <li>{t("terms:s3.li3")}</li>
              <li>{t("terms:s3.li4")}</li>
              <li>{t("terms:s3.li5", { siteName: POLICY.siteName })}</li>
            </ul>
          </div>

          {/* 4 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s4.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s4.li1")}</li>
              <li>{t("terms:s4.li2", { siteName: POLICY.siteName })}</li>
              <li>{t("terms:s4.li3", { siteName: POLICY.siteName })}</li>
            </ul>
          </div>

          {/* 5 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Share2 className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s5.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s5.li1")}</li>
              <li>{t("terms:s5.li2")}</li>
              <li>{t("terms:s5.li3", { siteName: POLICY.siteName })}</li>
              <li>{t("terms:s5.li4")}</li>
            </ul>
          </div>

          {/* 6 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Crown className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s6.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s6.li1", { free: POLICY.freePlayLimit })}</li>
              <li>{t("terms:s6.li2", { teams: POLICY.premiumTeamsLimit })}</li>
              <li>{t("terms:s6.li3")}</li>
            </ul>
          </div>

          {/* 7 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Lock className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s7.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:s7.body")}
            </p>
          </div>

          {/* 8 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Wrench className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s8.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s8.li1")}</li>
              <li>{t("terms:s8.li2")}</li>
            </ul>
          </div>

          {/* 9 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <RefreshCcw className="h-5 h-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s9.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:s9.body", { siteName: POLICY.siteName })}
            </p>
          </div>

          {/* 10 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <TriangleAlert className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s10.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s10.li1")}</li>
              <li>{t("terms:s10.li2")}</li>
              <li>{t("terms:s10.li3", { siteName: POLICY.siteName })}</li>
            </ul>
          </div>

          {/* 11 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Ban className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s11.title")}</h2>
            </header>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
              <li>{t("terms:s11.li1")}</li>
              <li>{t("terms:s11.li2", { siteName: POLICY.siteName })}</li>
            </ul>
          </div>

          {/* 12 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s12.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:s12.body", { siteName: POLICY.siteName })}
            </p>
          </div>

          {/* 13 */}
          <div className="rounded-xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-3 flex items-center gap-2">
              <Scale className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h2 className="text-lg font-semibold">{t("terms:s13.title")}</h2>
            </header>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:s13.body")}
            </p>
          </div>

          {/* Contact / Footer */}
          <div className="rounded-2xl border border-white/40 bg-white/70 p-6 text-center shadow-md backdrop-blur-md dark:border-white/5 dark:bg-white/5">
            <div className="mb-2 inline-flex items-center gap-2">
              <Mail className="h-4 w-4 text-orange-600 dark:text-orange-300" />
              <span className="text-sm font-medium">{t("terms:footer.support")}</span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("terms:footer.contact", { email: POLICY.contactEmail })}
            </p>
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {t("terms:footer.lastUpdate", { date: POLICY.lastUpdate })}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReglesUtilisation;
